import { CalendarClock, MessagesSquare, User } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { useDiscussoesControllerListar } from '@/api/generated/discussões-técnicas/discussões-técnicas';
import { CLASSE_STATUS, ROTULO_STATUS } from '@/features/discussoes/rotulos';
import { formatarDataHora } from '@/features/historico/rotulos';
import { EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { rotaDaDiscussao } from '@/shared/lib/rotas';
import { Badge } from '@/shared/ui/badge';
import { Paginacao } from '@/shared/ui/pagination';
import { Skeleton } from '@/shared/ui/skeleton';

const POR_PAGINA = 12;

/**
 * Discussões técnicas do paciente, dentro do histórico.
 *
 * Reaproveita o mesmo `GET /discussoes` da tela `DiscussoesPage` — só que
 * filtrado por `pacienteId` — porque é a listagem tenant-escopada do próprio
 * médico: nenhuma regra de acesso nova, o paciente já é dele. Cards em vez de
 * tabela porque aqui não há colunas para comparar, e sim histórico para
 * folhear: uma discussão aberta ou já encerrada, com quem ela foi.
 */
export function AbaEspecialistaAssociado({ pacienteId }: { pacienteId: string }) {
  const [pagina, setPagina] = useState(1);

  const { data, isPending, isError, refetch } = useDiscussoesControllerListar({
    pacienteId,
    pagina,
    porPagina: POR_PAGINA,
    ordenarPor: 'abertaEm',
    ordem: 'desc',
  });

  if (isPending) {
    return (
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-32 rounded-lg" />
        ))}
      </div>
    );
  }

  if (isError || !data) {
    return <EstadoErro aoTentarDeNovo={() => void refetch()} />;
  }

  if (data.itens.length === 0) {
    return (
      <div className="bg-surface-raised rounded-lg border-2 border-brand/20">
        <EstadoVazio
          icone={MessagesSquare}
          titulo="Nenhuma discussão técnica"
          descricao="As discussões abertas com um especialista sobre este paciente aparecem aqui."
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {data.itens.map((discussao) => (
          <Link
            key={discussao.id}
            to={rotaDaDiscussao(discussao.id)}
            className="flex flex-col gap-2 rounded-lg border-2 border-brand/20 bg-surface-raised p-4 transition-colors hover:border-brand/40"
          >
            <div className="flex items-start justify-between gap-2">
              <span className="font-mono text-xs text-muted-foreground">{discussao.codigo}</span>
              <Badge variant="outline" className={CLASSE_STATUS[discussao.status]}>
                {ROTULO_STATUS[discussao.status] ?? discussao.status}
              </Badge>
            </div>

            <p className="font-medium text-foreground line-clamp-2">{discussao.assunto}</p>

            <div className="mt-auto space-y-1 pt-2 text-xs text-muted-foreground">
              <p className="flex items-center gap-1.5">
                <User className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                {discussao.assessorNome}
              </p>
              <p className="flex items-center gap-1.5">
                <CalendarClock className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                {discussao.encerradaEm
                  ? `Encerrada em ${formatarDataHora(discussao.encerradaEm)}`
                  : `Aberta em ${formatarDataHora(discussao.abertaEm)}`}
              </p>
            </div>
          </Link>
        ))}
      </div>

      <Paginacao
        pagina={data.pagina}
        totalPaginas={data.totalPaginas}
        total={data.total}
        porPagina={data.porPagina}
        aoMudarPagina={setPagina}
      />
    </div>
  );
}
